import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useMyFeedback, type FeedbackStatus } from "@/hooks/use-my-feedback";

// Status-etiketter (konsistent med admin-siden): acknowledged=Løst, closed=Avsluttet.
const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: "Ny",
  acknowledged: "Løst",
  closed: "Avsluttet",
  deleted: "Slettet",
};
const STATUS_COLOR: Record<FeedbackStatus, string> = {
  new: "bg-amber-100 text-amber-800",
  acknowledged: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-100 text-slate-700",
  deleted: "bg-muted text-muted-foreground",
};

export default function MineTilbakemeldingerPage() {
  const { activeUser } = useImpersonation();
  const { data: rows, isLoading, error } = useMyFeedback(activeUser?.id ?? null);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");

  return (
    <div className="page-container animate-fade-in">
      <div className="flex items-center gap-2 mb-6">
        <SrIcon name="message-square" className="w-6 h-6 text-muted-foreground" />
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-wide text-foreground">
          Mine tilbakemeldinger
        </h1>
      </div>

      <Card>
        <CardContent className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-destructive">Feil: {(error as Error).message}</p>
          ) : !rows || rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Du har ikke sendt inn noen tilbakemeldinger ennå.
            </p>
          ) : (
            <div className="space-y-3">
              {rows.map((f) => (
                <div
                  key={f.id}
                  className={cn(
                    "p-4 rounded-lg border transition-colors",
                    f.id === highlightId
                      ? "border-primary bg-primary/5"
                      : "border-border/50 bg-card/50",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-foreground whitespace-pre-wrap">{f.body}</p>
                    <span
                      className={cn(
                        "shrink-0 text-xs px-2 py-0.5 rounded font-medium",
                        STATUS_COLOR[f.status],
                      )}
                    >
                      {STATUS_LABEL[f.status]}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Sendt {formatDate(f.created_at)}
                    {(f.status === "acknowledged" || f.status === "closed") && f.handled_at
                      ? ` · ${STATUS_LABEL[f.status]} ${formatDate(f.handled_at)}`
                      : ""}
                  </p>
                  {f.replies.length > 0 && (
                    <div className="mt-3 space-y-2 border-t border-border/40 pt-3">
                      {f.replies.map((r) => (
                        <div key={r.id} className="text-sm">
                          <span className="font-medium text-foreground">
                            Svar fra {r.authorName}:
                          </span>{" "}
                          <span className="text-foreground whitespace-pre-wrap">{r.body}</span>
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            {formatDate(r.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
